import { vec3 } from 'gl-matrix';
import { useMemo } from 'react';
import { paneImageOf } from '~/engine/view/paneImage';
import { useFusedImageDisplayVersion } from '~/hooks/environment/useEnvironmentViews';
import { RenderAdapter } from '~/viewportGrid/adapters/types';
import { useChangeVersion } from '~/viewportGrid/adapters/useChangeVersion';
import { planeViewOf } from '../ports/PlaneViewRenderAdapter';

/** Faint, so the outline marks the edge without competing with the image. */
const OUTLINE_OPACITY = 0.4;

interface TileImageOutlineMountProps {
    renderer: RenderAdapter;
}

/**
 * Outlines, dashed, where the displayed plane cuts the image a tile draws,
 * so the image edge shows against padding as dark as the image. Follows
 * scrolls, pans, zooms and a change of the image drawn. Renders nothing for
 * non-engine renderers (the mock adapters) or where the plane misses the
 * image.
 */
export default function TileImageOutlineMount({
    renderer,
}: TileImageOutlineMountProps) {
    useChangeVersion(renderer.getCursorSource());
    useChangeVersion(renderer.getDisplayScaleSource());
    const view = planeViewOf(renderer);
    useFusedImageDisplayVersion(view?.environment);
    const pane = view && paneImageOf(view);
    const grid = pane && pane.kind !== 'none' ? pane.grid : undefined;
    const [normalX, normalY, normalZ] = view?.viewDirection ?? [0, 0, 1];
    const offsetMm = view?.planeCoordinates ?? 0;
    // Recut only when the image or the displayed plane changes.
    const sectionMm = useMemo(() => {
        const normal = vec3.fromValues(normalX, normalY, normalZ);
        const point = vec3.scale(vec3.create(), normal, offsetMm);
        return grid?.planeCutMm({ point, normal }) ?? [];
    }, [grid, normalX, normalY, normalZ, offsetMm]);
    if (!view || sectionMm.length === 0) {
        return null;
    }
    return (
        <svg
            data-testid="tile-image-outline"
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 4,
            }}
        >
            <polygon
                points={sectionMm
                    .map((corner) =>
                        view.canvasCoordsFromPatientCoords(corner).join(',')
                    )
                    .join(' ')}
                fill="none"
                stroke="#ffffff"
                strokeDasharray="4 3"
                opacity={OUTLINE_OPACITY}
            />
        </svg>
    );
}
